// ==================== FILE: src/config/SectionRegistry.js ====================
// Centralized Section Registry — Single source of truth for ALL landing-page sections.
//
// HOW TO ADD A NEW SECTION:
// 1. Add an entry to SECTION_REGISTRY below with the section's metadata.
// 2. Register the component in ComponentRegistry.js.
// 3. Add the section type string to the template's `defaultSections` array
//    inside TemplateStyles.js (for each template that should show it).
// That's it — the Header nav and Landing page will pick it up automatically.

/**
 * Master registry of every possible landing-page section.
 *
 * Each key is the canonical section `type` string used everywhere:
 *   - TemplateStyles.js  → defaultSections[].type
 *   - ComponentRegistry.js → ComponentMap key
 *   - restaurantConfig.js  → config.sections[].type
 *
 * Properties:
 *   @property {string}  navLabel   – Text shown in the header nav link.
 *   @property {string}  anchorId   – DOM id on the section wrapper (used as #hash target).
 *   @property {boolean} showInNav  – Whether this section should generate a header nav item.
 *                                    Set to false for decorative/utility sections like TrustBadges.
 *   @property {number}  [navOrder] – Optional override for nav ordering. Lower = earlier.
 *                                    When omitted, nav order follows the template's defaultSections order.
 */
export const SECTION_REGISTRY = {
  Banner: {
    navLabel: 'HOME',
    anchorId: 'banner',
    showInNav: true,
  },
  TrustBadges: {
    navLabel: 'TRUST',
    anchorId: 'trust-badges',
    showInNav: false, // Decorative strip — no nav link needed
  },
  About: {
    navLabel: 'OUR STORY',
    anchorId: 'about',
    showInNav: true,
  },
  SpecialMenu: {
    navLabel: 'SPECIALS',
    anchorId: 'special-menu',
    showInNav: false, // Usually folded under "OUR STORY" or "GALLERY" in nav
  },
  MenuSection: {
    navLabel: 'MENU',
    anchorId: 'menu',
    showInNav: false, // The real MENU link goes to /restaurants, handled separately
  },
  GallerySection: {
    navLabel: 'GALLERY',
    anchorId: 'gallery',
    showInNav: true,
  },
  TeamSection: {
    navLabel: 'OUR TEAM',
    anchorId: 'our_team',
    showInNav: true,
  },
  BlogSection: {
    navLabel: 'BLOG',
    anchorId: 'blog',
    showInNav: true,
  },
  ReservationForm: {
    navLabel: 'RESERVATIONS',
    anchorId: 'reservation',
    showInNav: false,
  },
  MapSection: {
    navLabel: 'LOCATION',
    anchorId: 'map',
    showInNav: true,
  },
  FounderSection: {
    navLabel: 'FOUNDER',
    anchorId: 'founder',
    showInNav: false,
  },
  ProcessSection: {
    navLabel: 'PROCESS',
    anchorId: 'process',
    showInNav: false,
  },
  StatsSection: {
    navLabel: 'STATS',
    anchorId: 'stats',
    showInNav: false,
  },
  TestimonialSection: {
    navLabel: 'TESTIMONIALS',
    anchorId: 'testimonials',
    showInNav: false,
  },
  CategoryGrid: {
    navLabel: 'CATEGORIES',
    anchorId: 'categories',
    showInNav: false,
  },
  AnnouncementBar: {
    navLabel: 'ANNOUNCEMENT',
    anchorId: 'announcement',
    showInNav: false, // Slim utility strip — no nav link needed
  },
  SweetPromoBanner: {
    navLabel: 'GIFTING',
    anchorId: 'promo',
    showInNav: false,
  },
  FinalCTA: {
    navLabel: 'ORDER',
    anchorId: 'final-cta',
    showInNav: false,
  },
};

// ──────────────────────────────────────────────────────────
// Helper: Build the `sections` array from a template's defaultSections
// ──────────────────────────────────────────────────────────

/**
 * Takes a template's `defaultSections` array (e.g. [{ type: "Banner" }, ...])
 * and returns only the entries whose `type` exists in the SECTION_REGISTRY.
 * This is what config.sections should be set to.
 *
 * @param {Array<{type: string}>} templateSections - The template's defaultSections.
 * @returns {Array<{type: string}>} Validated and ordered sections.
 */
export const buildSectionsFromTemplate = (templateSections) => {
  if (!templateSections || !Array.isArray(templateSections)) {
    // Fallback: return a sensible default
    return [
      { type: 'Banner' },
      { type: 'About' },
      { type: 'MenuSection' },
    ];
  }

  return templateSections.filter(section => SECTION_REGISTRY[section.type]);
};

// ──────────────────────────────────────────────────────────
// Helper: Build `navLinks` from a template's defaultSections
// ──────────────────────────────────────────────────────────

/**
 * Auto-generates the header navLinks array from a template's defaultSections.
 *
 * Logic:
 *   1. Iterate through the template sections in order.
 *   2. For each section with `showInNav: true` in the registry, add a nav link.
 *   3. Append the fixed "MENU" link (→ /restaurants) at the end.
 *   4. Append the fixed "CONTACT" link (→ #footer) at the end.
 *
 * Template-level overrides:
 *   Sections in defaultSections can include `{ type: "About", navLabel: "ABOUT US", showInNav: true }`
 *   to override the registry defaults per-template.
 *
 * @param {Array<{type: string, navLabel?: string, showInNav?: boolean}>} templateSections
 * @returns {Array<{name: string, href: string, isOrderButton?: boolean}>}
 */
export const buildNavLinksFromSections = (templateSections) => {
  const navLinks = [];

  if (templateSections && Array.isArray(templateSections)) {
    for (const section of templateSections) {
      const registry = SECTION_REGISTRY[section.type];
      if (!registry) continue;

      // Allow per-template overrides
      const showInNav = section.showInNav !== undefined ? section.showInNav : registry.showInNav;
      if (!showInNav) continue;

      const label = section.navLabel || registry.navLabel;
      const anchorId = section.anchorId || registry.anchorId;

      navLinks.push({
        name: label,
        href: `#${anchorId}`,
      });
    }
  }

  // Always append the fixed "MENU" and "CONTACT" links
  navLinks.push({ name: 'MENU', href: '/restaurants' });
  navLinks.push({ name: 'CONTACT', href: '#footer' });

  return navLinks;
};

/**
 * Get the anchor ID for a given section type.
 * Useful when components need to know their own anchor ID.
 *
 * @param {string} sectionType - e.g. "GallerySection"
 * @returns {string} The anchor ID, e.g. "gallery"
 */
export const getSectionAnchorId = (sectionType) => {
  return SECTION_REGISTRY[sectionType]?.anchorId || sectionType.toLowerCase();
};

export default SECTION_REGISTRY;
